// Conexión al WebSocket de ubicaciones. Ver CLAUDE.md §7.
//
// Reconecta si el socket se cae o si el celular pasó a segundo plano y regresa
// (visibilitychange). Así el turno no "muere en silencio" al cambiar de app. El
// handler se guarda en un ref para no reconectar en cada render.
import { useCallback, useEffect, useRef } from 'react'
import { getToken } from '@/googleAuth'

type Handler = (msg: unknown) => void

async function buildWsUrl(): Promise<string> {
  const token = getToken()
  if (!token) throw new Error('Sin token de sesión')
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}/ws/locations?token=${token}`
}

export function useLocationsSocket(onMessage: Handler, enabled = true) {
  const wsRef = useRef<WebSocket | null>(null)
  // Guarda el handler en un ref para no reconectar en cada render.
  const handlerRef = useRef(onMessage)
  handlerRef.current = onMessage

  useEffect(() => {
    if (!enabled) return
    let closed = false
    let retry: ReturnType<typeof setTimeout> | null = null

    const connect = async () => {
      if (closed) return
      // No dupliques si ya hay una conexión viva o en curso.
      const cur = wsRef.current
      if (cur && (cur.readyState === WebSocket.OPEN || cur.readyState === WebSocket.CONNECTING)) return
      try {
        const url = await buildWsUrl() // token fresco en cada intento
        if (closed) return
        const ws = new WebSocket(url)
        ws.onmessage = (e) => {
          try {
            handlerRef.current(JSON.parse(e.data))
          } catch {
            /* ignora mensajes no-JSON */
          }
        }
        ws.onclose = () => {
          if (closed) return
          if (retry) clearTimeout(retry)
          retry = setTimeout(connect, 3000) // reintenta mientras siga activo
        }
        wsRef.current = ws
      } catch {
        // Sin token / sin red: reintenta más tarde.
        if (closed) return
        if (retry) clearTimeout(retry)
        retry = setTimeout(connect, 5000)
      }
    }

    // Al volver a primer plano, reconecta si el socket murió en segundo plano.
    const onVisible = () => {
      if (document.visibilityState === 'visible') connect()
    }

    connect()
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      closed = true
      document.removeEventListener('visibilitychange', onVisible)
      if (retry) clearTimeout(retry)
      const ws = wsRef.current
      wsRef.current = null
      if (ws) {
        ws.onclose = null // evita que el cierre por cleanup dispare una reconexión
        ws.close()
      }
    }
  }, [enabled])

  // Enviar la posición propia (instalador/quien comparte turno). Memoizado (ref estable).
  const sendLocation = useCallback((lat: number, lng: number, accuracy?: number) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'location_update', lat, lng, accuracy }))
    }
  }, [])

  // Avisar cierre de turno (para que los mapas quiten al usuario de inmediato).
  const sendShiftEnd = useCallback(() => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'shift_end' }))
    }
  }, [])

  return { sendLocation, sendShiftEnd }
}
